// src/dnd/views/Sidebar.js
import React, { useEffect, useState } from "react";
import Tabs from "./Sidebar_parts/Tabs";
import DiceTab from "./Sidebar_parts/DiceTab";
import CharactersTab from "./Sidebar_parts/CharactersTab";
import MapInfoTab from "./Sidebar_parts/MapInfoTab";
import MapControlsTab from "./Sidebar_parts/MapControlsTab";
import MapPresenceAssignmentsPanel from "./Sidebar_parts/MapPresenceAssignmentsPanel";

const TIP_BAR_HEIGHT = 68;
const TAB_KEY = "point_atlas_sidebar_active_tab_v2";

export default function Sidebar(props) {
  const {
    sidebarWidth,
    sidebarRef,
    logo,
    mapDisplayName,
    gameName,
    styles = {},
    handleMouseDown,
    isMobile = false,
    isMapOwner = false,
    customCategories = [],
    customTypes = {},
    typeColours = {},
    visibleTypes = new Set(),
    visibleCategories = new Set(),
    expandedCategories = {},
    setExpandedCategories = () => {},
    expandedTypes = {},
    setExpandedTypes = () => {},
    toggleAllTypes = () => {},
    allTypesVisible = true,
    toggleCategoryExpand = () => {},
    toggleCategory = () => {},
    toggleType = () => {},
    pois = [],
    zones = [],
    visibleZones = new Set(),
    setVisibleZones = () => {},
    hoveredZoneId,
    setHoveredZoneId = () => {},
    poiLabelToggleBtn,
    onMobileTypeClick,
    onClose,
    hoveredType,
    hoveredPoiId,
    setHoveredPoiId = () => {},
    handleTypeHover,
    handleTypeUnhover,
    setHideSidebarHover,
    extraRegionMaps = [],
    onAddNewRegionMap,
    onRegionMapClick,
    parentMapId,
    showAllPoiLabels = false,
    showPoiNames = false,
    setShowPoiNames = () => {},
    onDiceRoll,
    user,
    mapId,
    characters = [],
    onCreateCharacter = () => {},
    onUploadCharacters = () => {},
    mapPresenceUsers = [],
    onlineMapUserIds = new Set(),
    mapPresenceUpdatedAt = 0,
    proximityVoiceEnabled = false,
    proximityVoiceSaving = false,
    proximityVoiceStatus = "",
    onToggleProximityVoice = () => {},
  } = props;

  const canManage = !!isMapOwner;
  const TABS = canManage
    ? ["Dice", "Characters", "Battle", "Map Controls"]
    : ["Dice", "Characters", "Battle", "Map Info"];

  const [activeTab, setActiveTab] = useState(() => {
    try {
      const saved = localStorage.getItem(TAB_KEY);
      return saved && TABS.includes(saved) ? saved : TABS[0];
    } catch {
      return TABS[0];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(TAB_KEY, activeTab);
    } catch {}
  }, [activeTab]);

  useEffect(() => {
    setActiveTab((cur) => (TABS.includes(cur) ? cur : TABS[0]));
  }, [canManage]);

  const hasTitleImage = Boolean(styles?.title_image_url);
  const sidebarBg = styles.sidebar_bg_colour || "#202124";
  const mainFont = styles.font_family || "inherit";
  const fontColour = styles.font_colour || "#fff";
  const watermarkUrl = styles?.watermark_url;

  return (
    <div
      className={`sidebar${hasTitleImage ? " has-title-image" : ""}`}
      style={{
        width: isMobile ? "100vw" : "100%",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        position: isMobile ? "fixed" : "relative",
        top: 0,
        left: 0,
        background: sidebarBg,
        fontFamily: mainFont,
        color: fontColour,
        ...(isMobile ? { zIndex: 2000 } : {}),
        ...(isMobile && {
          minWidth: 0,
          maxWidth: "100vw",
          padding: 0,
          borderRadius: 0,
          boxShadow: "none",
        }),
        overflow: "hidden",
      }}
      ref={sidebarRef}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginBottom: hasTitleImage ? 0 : 5,
          paddingTop: hasTitleImage ? 0 : 12,
          width: "100%",
          paddingLeft: hasTitleImage ? 0 : 5,
          paddingRight: hasTitleImage ? 0 : 12,
          boxSizing: "border-box",
          userSelect: "none",
        }}
      >
        {logo && (
          <a
            href="/"
            title="Go to Home"
            style={{ marginBottom: hasTitleImage ? -100 : -50 }}
          >
            <img
              src={logo}
              alt="Point Atlas Logo"
              style={{
                maxWidth: 50,
                maxHeight: 50,
                width: "auto",
                height: "auto",
                display: "block",
                cursor: "pointer",
                marginBottom: 50,
                marginTop: 20,
              }}
            />
          </a>
        )}

        {hasTitleImage ? (
          <img
            src={styles.title_image_url}
            alt="Game Logo"
            style={{
              width: "80%",
              maxHeight: 150,
              objectFit: "fill",
              display: "block",
              marginTop: 0,
              marginBottom: 0,
              userSelect: "none",
              pointerEvents: "none",
            }}
          />
        ) : (
          <span
            style={{
              fontWeight: "bold",
              fontSize: "1.9em",
              margin: "4px 0 0 0",
              letterSpacing: "1px",
              color: fontColour,
              textAlign: "center",
              width: "100%",
              fontFamily: mainFont,
              userSelect: "none",
            }}
          >
            {styles.title_text || mapDisplayName}
          </span>
        )}

        {gameName && !hasTitleImage && (
          <div
            style={{
              fontSize: "1.1em",
              fontWeight: 600,
              color: "#ffc73c",
              marginTop: 2,
              marginBottom: 6,
              textTransform: "capitalize",
              textAlign: "center",
              width: "100%",
              userSelect: "none",
            }}
          >
            {gameName}
          </div>
        )}

        {styles?.creator_signature && (
          <div
            style={{
              color: "#ccc",
              fontSize: 13,
              fontWeight: "bold",
              margin: "4px 0 0 0",
              fontStyle: "italic",
              textAlign: "center",
              width: "90%",
              letterSpacing: "0.5px",
              opacity: 0.82,
              userSelect: "none",
            }}
          >
            {styles.creator_signature}
          </div>
        )}
      </div>

      <Tabs tabs={TABS} active={activeTab} onChange={(t) => setActiveTab(t)} />

      <div
        style={{
          flex: 1,
          display: "flex",
          minHeight: 0,
          position: "relative",
        }}
      >
        {activeTab === "Dice" && (
          <DiceTab
            styles={styles}
            TIP_BAR_HEIGHT={TIP_BAR_HEIGHT}
            onDiceRoll={onDiceRoll}
          />
        )}

        {activeTab === "Characters" && (
          <CharactersTab
            styles={styles}
            TIP_BAR_HEIGHT={TIP_BAR_HEIGHT}
            isMobile={isMobile}
            fontColour={fontColour}
            mainFont={mainFont}
            characters={characters}
            onCreateCharacter={onCreateCharacter}
            onUploadCharacters={onUploadCharacters}
          />
        )}

        {activeTab === "Battle" && (
          <div style={{ flex: 1, overflowY: "auto", padding: 12 }}>
            <div style={{ opacity: 0.8 }}>Battle stats &amp; trackers coming soon.</div>
          </div>
        )}

        {!canManage && activeTab === "Map Info" && (
          <MapInfoTab
            styles={styles}
            TIP_BAR_HEIGHT={TIP_BAR_HEIGHT}
            pois={pois}
            zones={zones}
            visibleZones={visibleZones}
            typeColours={typeColours}
            isMapOwner={isMapOwner}
            onAddNewRegionMap={onAddNewRegionMap}
            mapPresenceUsers={mapPresenceUsers}
            onlineMapUserIds={onlineMapUserIds}
            mapPresenceUpdatedAt={mapPresenceUpdatedAt}
          />
        )}

        {canManage && activeTab === "Map Controls" && (
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <MapPresenceAssignmentsPanel
              mapId={mapId}
              mapPresenceUsers={mapPresenceUsers}
              onlineMapUserIds={onlineMapUserIds}
              mapPresenceUpdatedAt={mapPresenceUpdatedAt}
              styles={styles}
              fontColour={fontColour}
              mainFont={mainFont}
              isMobile={isMobile}
            />

            <div style={{ flex: 1, minHeight: 0 }}>
              <MapControlsTab
                {...{
                  user,
                  customCategories,
                  customTypes,
                  typeColours,
                  visibleTypes,
                  visibleCategories,
                  expandedCategories,
                  setExpandedCategories,
                  expandedTypes,
                  setExpandedTypes,
                  toggleAllTypes,
                  allTypesVisible,
                  toggleCategoryExpand,
                  toggleCategory,
                  toggleType,
                  pois,
                  zones,
                  visibleZones,
                  setVisibleZones,
                  hoveredZoneId,
                  setHoveredZoneId,
                  poiLabelToggleBtn,
                  isMobile,
                  onMobileTypeClick,
                  onClose,
                  hoveredType,
                  hoveredPoiId,
                  setHoveredPoiId,
                  handleTypeHover,
                  handleTypeUnhover,
                  setHideSidebarHover,
                  isMapOwner,
                  extraRegionMaps,
                  onAddNewRegionMap,
                  onRegionMapClick,
                  parentMapId,
                  styles,
                  fontColour,
                  mainFont,
                  TIP_BAR_HEIGHT,
                  mapDisplayName,
                  showAllPoiLabels,
                  showPoiNames,
                  setShowPoiNames,
                  mapId,
                  mapPresenceUsers,
                  onlineMapUserIds,
                  mapPresenceUpdatedAt,
                  proximityVoiceEnabled,
                  proximityVoiceSaving,
                  proximityVoiceStatus,
                  onToggleProximityVoice,
                }}
              />
            </div>
          </div>
        )}
      </div>

      {watermarkUrl && (
        <img
          src={watermarkUrl}
          alt="Map Watermark"
          style={{
            position: "absolute",
            bottom: 32 + TIP_BAR_HEIGHT,
            left: "50%",
            transform: "translateX(-50%)",
            width: "85%",
            maxWidth: 330,
            opacity: 0.1,
            pointerEvents: "none",
            userSelect: "none",
            zIndex: 0,
          }}
        />
      )}
    </div>
  );
}